// Device scenarios with gist-verbatim congruence classification
// Updated with 2024-2025 modern threat vectors
// Congruent: Gist cues align with verbatim cues (looks risky + is risky, or looks safe + is safe)
// Incongruent: Gist cues conflict with verbatim cues (looks safe + is risky, or looks risky + is safe)

export interface DeviceScenario {
  id: string;
  domain: string; // DDom1-DDom8: Counterbalancing domain for experimental design
  name: string;
  type: 'download' | 'connection' | 'software' | 'network' | 'permission' | 'library' | 'apikey' | 'qr-code' | 'voice-message' | 'mfa';
  description: string;
  warningMessage?: string;
  isRisky: boolean;
  hasWarning: boolean;
  location: string;
  details?: string;
  timeFound?: string;
  source?: string;
  action?: string | null;
  actionObj?: {
    text: string;
    consequence?: string;
  };

  // Congruence classification
  congruence: 'congruent' | 'incongruent';

  // Cues for FTT analysis
  gistCues: string[];
  verbatimCues: string[];

  // Additional metadata
  threatIndicators?: string[];
  safetyIndicators?: string[];
  conflictLevel: 'low' | 'medium' | 'high';
  complexity: number;

  // Visual rendering data (for DeviceScenarioVisual component)
  visualData?: {
    type: 'system-notification' | 'browser-warning' | 'usb-dialog' | 'network-selection' | 'software-install' | 'email-qr' | 'voice-message' | 'mfa-notifications' | 'antivirus-alert' | 'email-attachment-preview' | 'vscode-extension' | 'file-download-prompt';
    title?: string;
    message?: string;
    icon?: string;
    buttons?: string[];
    url?: string;
    details?: string[];
    warningLevel?: 'low' | 'medium' | 'high';
    sender?: string;
    subject?: string;
    qrCodeUrl?: string;
    voiceTranscript?: string;
    notificationCount?: number;
    networks?: Array<{name: string; security: string; strength: string; highlighted?: boolean}>;
    publisher?: string;
    fileSize?: string;
    fileName?: string;
    attachmentType?: string;
    codeSnippet?: string;
    extensionName?: string;
    sourceUrl?: string;
  };
}

export const deviceScenarios: DeviceScenario[] = [
  // CONGRUENT SAFE DEVICES (looks safe + is safe)
  {
    id: 'd2',
    domain: 'DDom1', // System Updates & IT Security
    name: 'IT Department Scanner',
    type: 'software',
    description: 'You\'re at your desk Monday morning. A popup appears: "IT Security Scanner v2.4 - Required Installation." It appeared right after login. You have a client meeting in 1 hour and need your computer working. Install will take 15 minutes and require restart.',
    warningMessage: 'Installation requires administrator privileges. Internal code signing verified.',
    isRisky: false,
    hasWarning: true,
    location: 'Company software portal',
    source: 'Internal IT ticket #2024-1158',
    details: 'Quarterly security audit tool. IT sent email last week mentioning mandatory scanner installation by end of month. Software shows: Publisher: Company IT Department, Internal code signing verified, Ticket #2024-1158. Company policy requires all employees install security scanner quarterly. Your colleague mentioned getting the same popup this morning.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['internalIT', 'ticketReference', 'companyPortal'],
    gistCues: ['IT department', 'Security software', 'Official popup', 'Colleague confirmed'],
    verbatimCues: ['Valid ticket number', 'Internal portal URL', 'IT email verified', 'Company policy requires this'],
    conflictLevel: 'low',
    complexity: 0.3,
    visualData: {
      type: 'software-install',
      title: 'IT Security Scanner v2.4',
      message: 'The IT Department requires installation of security scanning software for quarterly compliance audit.',
      publisher: 'Company IT Department',
      buttons: ['Install Verified Scanner', 'Do Not Install'],
      details: [
        'Publisher: Company IT Department',
        'Verified: Internal code signing',
        'Ticket: #2024-1158',
        'Size: 45.2 MB',
        'Installation time: ~15 minutes',
        '⚠️ Computer will restart after installation'
      ]
    }
  },

  // CONGRUENT RISKY DEVICES (looks risky + is risky)
  {
    id: 'd3',
    domain: 'DDom2', // Malicious Software & Fake Updates
    name: 'Free PDF Converter Download',
    type: 'download',
    description: 'You need to convert a client document to PDF before a meeting. A web search led to docs-convert-free.net, which downloaded "FreePDFConverter.exe." Windows Defender blocks the file and reports a Trojan before it opens.',
    warningMessage: 'Windows Defender has blocked this file - potential malware detected',
    isRisky: true,
    hasWarning: true,
    location: 'Company laptop - Downloads folder',
    source: 'docs-convert-free.net',
    details: 'Windows Defender blocked the executable before it ran. The download has no verified publisher, came from an external conversion site, and was detected as Trojan:Win32/Wacatac.B!ml. The requested work can be completed with the company-approved PDF software already installed.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['unapprovedDownload', 'malwareWarning', 'unknownPublisher', 'blocked'],
    gistCues: ['Free converter', 'Unexpected executable', 'Blocked by antivirus', 'External download site'],
    verbatimCues: ['External conversion domain', 'Windows Defender blocked the file', 'Trojan detected', 'No verified publisher'],
    conflictLevel: 'low',
    complexity: 0.2,
    visualData: {
      type: 'antivirus-alert',
      title: 'Threat Detected',
      message: 'Windows Defender blocked this downloaded application before it opened.',
      warningLevel: 'high',
      fileName: 'FreePDFConverter.exe',
      fileSize: '2.4 MB',
      buttons: ['Run Downloaded File Anyway', 'Keep File Blocked'],
      details: [
        'Threat: Trojan:Win32/Wacatac.B!ml',
        'Source: docs-convert-free.net',
        'Risk Level: Severe',
        'Action: File blocked and quarantined',
        'Publisher: Unknown'
      ]
    }
  },

  {
    id: 'd11',
    domain: 'DDom3', // Network & WiFi Security
    name: 'Suspicious Company-Named Hotel WiFi',
    type: 'network',
    description: 'You\'re at a hotel while traveling for business, far from your organization\'s office. You need WiFi for a presentation. Your laptop shows "CompanyWiFi-Secure," even though that network should only be available inside the office. The hotel\'s posted network is "Hilton_Guest."',
    warningMessage: 'A company-named network is appearing far outside the company office.',
    isRisky: true,
    hasWarning: false,
    location: 'Business-trip hotel lobby',
    source: 'WiFi network selection',
    details: 'You are far from your organization\'s office. Hotel WiFi usually shows networks like "Hilton_Guest". Your organization uses the network name "CompanyWiFi_Secure" only at the office. This network shows as "CompanyWiFi-Secure" (with hyphen). Company IT policy says: "Never connect to company-named WiFi outside the office - use VPN over hotel WiFi instead."',
    action: null,
    actionObj: {
      text: 'Connect to this network',
      consequence: 'Man-in-the-middle attack (evil twin WiFi)'
    },
    congruence: 'congruent',
    threatIndicators: ['evilTwin', 'characterSpoofing', 'impossibleRange', 'publicSpace'],
    gistCues: ['Unexpected company network', 'Hotel setting', 'Possible impersonation', 'Urgent deadline'],
    verbatimCues: ['1,000 miles from office (impossible range)', 'Hyphen vs underscore difference', 'Hotel location (not office)', 'IT policy: VPN over hotel WiFi', 'Evil twin attacks common in hotels'],
    conflictLevel: 'high',
    complexity: 0.8,
    visualData: {
      type: 'network-selection',
      title: 'Available Networks',
      message: 'Select a WiFi network to connect',
      buttons: ['Connect to Unverified CompanyWiFi-Secure', 'Use Posted Hotel WiFi with VPN'],
      networks: [
        { name: 'CompanyWiFi-Secure', security: 'Secured · Unverified at this location', strength: '⚫⚫⚫⚫⚫' },
        { name: 'Hilton_Guest', security: 'Open · Name posted by hotel', strength: '⚫⚫⚫⚫◯' },
        { name: 'Hilton_Conference', security: 'Secured · WPA2', strength: '⚫⚫⚫◯◯' },
        { name: 'xfinitywifi', security: 'Open Network', strength: '⚫⚫◯◯◯' }
      ]
    }
  },

  {
    id: 'd12',
    domain: 'DDom4', // Multi-Factor Authentication
    name: 'MFA Push Notification Fatigue',
    type: 'mfa',
    description: 'It\'s 11:47 PM and you\'re about to sleep. Your phone starts buzzing repeatedly with 15+ Microsoft Authenticator notifications in 20 minutes. Each notification says "Sign-in request" with location showing "Moscow, Russia" and message: "⚠️ Stop these repeated alerts by approving this sign-in request."',
    warningMessage: 'Multiple MFA push notifications from foreign location',
    isRisky: true,
    hasWarning: true,
    location: 'Your bedroom, Boston MA (11:47 PM)',
    source: 'Microsoft Authenticator - work-email@company.com',
    details: 'You did NOT attempt to sign in tonight. Location shows Moscow, Russia (you\'re in Boston, USA). IP address: 185.220.101.47 (Russian IP block). Device: Windows 10 PC (you use Mac). Attacker has your password and is spamming MFA hoping you\'ll approve out of frustration.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['mfaFatigue', 'passwordCompromised', 'foreignIP', 'harassmentPattern'],
    gistCues: ['Notification harassment', 'Late night timing', '"Approve to stop alerts"', 'Exhausted and frustrated'],
    verbatimCues: ['Moscow, Russia location', 'Russian IP address', 'Wrong device type', 'No login attempt made', 'IT warned about MFA fatigue attacks'],
    conflictLevel: 'low',
    complexity: 0.3,
    visualData: {
      type: 'mfa-notifications',
      title: 'Microsoft Authenticator',
      message: '⚠️ Stop these repeated alerts by approving this sign-in request. If you approve, the notifications will stop immediately.',
      buttons: ['✓ APPROVE (Stop Alerts)', '✗ DENY'],
      notificationCount: 15,
      details: [
        'Account: work-email@company.com',
        'Location: Moscow, Russia',
        'IP Address: 185.220.101.47',
        'Device: Windows 10 PC',
        'Time: 11:47 PM (Local)'
      ]
    }
  },

  // INCONGRUENT SAFE DEVICES (looks risky but is safe)
  {
    id: 'd5',
    domain: 'DDom1', // System Updates & IT Security
    name: 'System Update Available',
    type: 'software',
    description: 'It\'s Thursday afternoon (Patch Tuesday week). A system notification appears: "URGENT: Critical Security Update - Installation Required by 5 PM Today." The notification says your computer will restart automatically and you\'ll lose unsaved work. You have a report due at 4 PM.',
    warningMessage: 'This update will restart your computer. Save all work before proceeding.',
    isRisky: false,
    hasWarning: true,
    location: 'IT push notification',
    source: 'Automated patch management system (SCCM)',
    details: 'Your company deploys Microsoft security patches monthly on the second Tuesday (Patch Tuesday). IT sent a Slack message this morning: "Deploying critical patches today, expect restart notifications." The update is coming from your company\'s internal SCCM deployment system (legitimate). However, the URGENT language and forced restart make you hesitate - is this really IT or is it ransomware using scare tactics?',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['legitimatePatch', 'internalSystem', 'scheduledUpdate'],
    gistCues: ['URGENT language', 'Forced restart threat', 'Deadline pressure', 'Report due soon'],
    verbatimCues: ['Patch Tuesday timing (2nd Thursday)', 'Internal SCCM system', 'IT Slack warning this morning', 'KB numbers shown'],
    conflictLevel: 'high',
    complexity: 0.8,
    visualData: {
      type: 'system-notification',
      title: 'URGENT: Critical Security Update',
      message: 'Critical security patches must be installed by 5:00 PM today to protect your system.',
      icon: '⚠️',
      buttons: ['Install Verified Update', 'Do Not Install'],
      details: [
        'Updates Available: 12 patches',
        'Includes: KB5034441, KB5034467 (Security)',
        'Source: Company SCCM (sccm.company.com)',
        'Deployment: Patch Tuesday (Monthly)',
        'Installation time: ~10 minutes',
        '',
        '⚠️ Your computer will restart automatically.',
        'Please save all work before proceeding.',
        '',
        'Deadline: Today at 5:00 PM',
        'IT Contact: helpdesk@company.com'
      ]
    }
  },

  {
    id: 'd6',
    domain: 'DDom7', // Workplace Equipment & Compliance
    name: 'Security_Audit_Tool.zip',
    type: 'permission',
    description: 'Email from Security Team: "Download quarterly audit tool from SharePoint by EOD." The file is Security_Audit_Tool.zip (87 MB). Antivirus pops up: "⚠️ This tool contains hacking utilities and may trigger alerts."',
    warningMessage: 'This tool may trigger antivirus alerts due to its security testing capabilities',
    isRisky: false,
    hasWarning: true,
    location: 'Security team SharePoint (sharepoint.company.com/security)',
    source: 'Internal security assessment request',
    details: 'Required for quarterly compliance (all employees participate). Source is internal SharePoint at https://sharepoint.company.com/security (verified URL). Security team confirmed via Slack they sent this email this morning. Pen-testing tools often trigger false positives because they probe for vulnerabilities. Company audit requires you to run this scan on your work laptop. The tool is digitally signed by "Company Security Team".',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['authorizedTool', 'internalRequest', 'auditPurpose'],
    gistCues: ['Hacking tool warning', 'Antivirus alert', 'Suspicious file name', 'Fear of malware'],
    verbatimCues: ['SharePoint.company.com (verified)', 'Security team Slack confirmation', 'Audit requirement', 'Digital signature present', 'Pen-testing tools trigger AV'],
    conflictLevel: 'high',
    complexity: 0.9,
    visualData: {
      type: 'file-download-prompt',
      title: 'Download Security_Audit_Tool.zip?',
      message: 'This file is from your company SharePoint. Your antivirus has detected security testing utilities.',
      fileName: 'Security_Audit_Tool.zip',
      fileSize: '87.3 MB',
      sourceUrl: 'sharepoint.company.com/security/audit',
      buttons: ['Download Verified Tool', 'Do Not Download'],
      details: [
        'Source: sharepoint.company.com/security',
        'Publisher: Company Security Team (Verified)',
        'Digital Signature: Valid',
        'File Type: ZIP Archive',
        '',
        '⚠️ Antivirus Warning:',
        'This archive contains penetration testing tools',
        'Tools may be flagged as: PUA (Potentially Unwanted)',
        '',
        'Purpose: Quarterly security audit',
        'Required: All employees by end of month',
        '',
        'Note: Pen-testing tools',
        'often trigger false positives.'
      ]
    }
  },

  // INCONGRUENT RISKY DEVICES (looks safe but is risky)
  {
    id: 'd7',
    domain: 'DDom2', // Malicious Software & Fake Updates
    name: 'Conference Display Firmware Update',
    type: 'software',
    description: 'The Samsung display in a conference room shows: "New Firmware Update Available - Version 2024.11.2." The screen has Samsung branding, but it asks you to download the update manually from samsung-updates.net.',
    warningMessage: 'Firmware update available for the conference-room display',
    isRisky: true,
    hasWarning: false,
    location: 'Workplace conference room',
    source: 'samsung-updates.net',
    details: 'The page uses Samsung branding, but the download domain is not samsung.com and the organization normally manages conference-room displays centrally. The prompt requests a manual executable download rather than an update through managed display settings.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['domainSpoofing', 'manualFirmwareDownload', 'unmanagedUpdate'],
    gistCues: ['Samsung logo', 'Normal update wording', 'Professional design', 'Picture-quality improvement'],
    verbatimCues: ['Download domain differs from samsung.com', 'Manual executable download', 'Conference displays are centrally managed'],
    conflictLevel: 'high',
    complexity: 0.85,
    visualData: {
      type: 'system-notification',
      title: 'Samsung Display Firmware Update',
      message: 'A new firmware update is available for the conference-room display.',
      icon: '📺',
      buttons: ['Download Firmware from This Page', 'Close Without Downloading'],
      details: [
        'Device: Samsung Conference Display',
        'Current Version: 2023.8.1',
        'New Version: 2024.11.2',
        'Size: 487 MB',
        '',
        'What\'s New:',
        '• Improved picture quality and HDR',
        '• New streaming apps added',
        '• Performance optimizations',
        '• Security updates',
        '',
        '⚠️ Download from: samsung-updates.net',
        'Installation method: Manual download',
        'Organization management: Conference displays are IT managed'
      ]
    }
  },

  // Based on 2024 O.MG cable attacks (USB data-stealing cables)
  {
    id: 'd13',
    domain: 'DDom5', // Physical Devices & USB
    name: 'USB-C Cable in Conference Room',
    type: 'connection',
    description: 'Your laptop is at 15% during a meeting. There\'s a white USB-C cable on the table with an Apple logo and a sticky note: "Charger for anyone to use!" Several colleagues have used it today.',
    warningMessage: 'Unknown USB cable available for charging',
    isRisky: true,
    hasWarning: false,
    location: 'Conference Room A - shared workspace',
    source: 'Unknown USB-C cable left on table',
    details: 'The cable looks exactly like an official Apple USB-C cable with proper branding and white finish. However, O.MG cables look identical to genuine cables but contain hidden chips that can inject keystrokes, steal data, and create backdoor access. Company IT policy (SEC-2024-08) explicitly states: "Never use unknown USB cables or chargers - they may contain data-stealing hardware." The sticky note and "anyone can use" messaging is a social engineering tactic. Unknown owner and no IT asset tag means this could be an O.MG cable planted by an attacker.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['omgCable', 'dataTheft', 'keystrokeInjection', 'unknownOwner', 'policyViolation'],
    gistCues: ['Apple logo looks genuine', 'Colleagues using it', 'Convenient charging', 'Low battery urgency', 'Sticky note says it\'s safe'],
    verbatimCues: ['Unknown owner (no asset tag)', 'O.MG cables look identical to genuine', 'IT policy: never use unknown USB devices', 'Can inject keystrokes/steal data', 'Company laptop has sensitive access'],
    conflictLevel: 'high',
    complexity: 0.9,
    visualData: {
      type: 'usb-dialog',
      title: 'Use Unknown USB-C Cable?',
      message: 'USB-C cable found in conference room. Cable has Apple logo and appears genuine.',
      icon: '🔌',
      buttons: ['Use This Cable', 'Do Not Use This Cable'],
      details: [
        'Location: Conference Room A table',
        'Type: USB-C to USB-C cable',
        'Appearance: White with Apple logo (looks genuine)',
        'Note attached: "Charger for anyone to use!"',
        '',
        'Current Situation:',
        '• Your battery: 15% remaining',
        '• Workshop duration: 1.5 hours left',
        '• Colleagues: Several have used this cable today',
        '',
        '⚠️ Security Risk Assessment:',
        'O.MG Cable Threat (2024):',
        '• Looks identical to real Apple cables',
        '• Contains hidden chip for keystroke injection',
        '• Can steal passwords, session tokens, files',
        '• Creates persistent backdoor access',
        '',
        '⚠️ Company IT Policy (SEC-2024-08):',
        '"Never use unknown USB cables or chargers.',
        'They may contain data-stealing hardware."',
        '',
        'Safer alternatives:',
        '• Use your own cable from laptop bag',
        '• Ask IT for approved charger (x5555)',
        '• Use wall outlet with your power adapter'
      ]
    }
  },

  {
    id: 'd14',
    domain: 'DDom6', // Browser Extensions & Plugins
    name: 'Crypto Price Tracker Browser Extension',
    type: 'software',
    description: 'A coworker shares a Chrome extension in Slack: "Crypto Price Tracker Pro" — 4.5 stars, 10K+ users. When you click Install, Chrome warns: "This extension can: Read and change all your data on all websites, See your browsing history."',
    warningMessage: 'Extension requests broad permissions including access to all websites',
    isRisky: true,
    hasWarning: true,
    location: 'Chrome Web Store - company laptop',
    source: 'Chrome extension "Crypto Price Tracker Pro"',
    details: 'Extension looks legitimate: 10K users, 4.5 stars, recent reviews. BUT: Developer name is "CryptoDev Solutions" (not a known company). Extension requests "Read all data on all websites" (can steal passwords, session tokens, form data). Your company laptop accesses internal admin panels and client databases. Similar-named legit extension exists: "Crypto Price Tracker" by "CoinMarketCap" with 500K users. IT policy forbids browser extensions without approval. This could be a credential stealer disguised as a simple price tracker.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['excessivePermissions', 'credentialStealer', 'lookalikeExtension', 'policyViolation'],
    gistCues: ['Coworker recommended', '10K users seems popular', '4.5 star rating', 'Simple useful tool', 'Chrome Web Store feels safe'],
    verbatimCues: ['Unknown developer (not CoinMarketCap)', 'Can read ALL websites (including banking, admin)', 'Real extension has 500K users (this has 10K)', 'IT policy: no unapproved extensions', 'Company laptop has internal access'],
    conflictLevel: 'high',
    complexity: 0.8,
    visualData: {
      type: 'vscode-extension',
      title: 'Add "Crypto Price Tracker Pro" to Chrome?',
      message: 'This extension will have access to your browsing data and website content.',
      extensionName: 'Crypto Price Tracker Pro',
      publisher: 'CryptoDev Solutions',
      buttons: ['Install This Extension', 'Do Not Install'],
      details: [
        'Extension: Crypto Price Tracker Pro',
        'Developer: CryptoDev Solutions',
        'Users: 10,284',
        'Rating: ★★★★☆ (4.5/5 - 847 reviews)',
        'Last Updated: 6 days ago',
        '',
        '⚠️ This extension can:',
        '• Read and change all your data on all websites',
        '• See your browsing history',
        '• Access data on all tabs',
        '',
        'Similar Extension:',
        'Crypto Price Tracker (Official)',
        'By: CoinMarketCap',
        'Users: 500,000+ (100x more)',
        '',
        '⚠️ Company IT Policy:',
        'Browser extensions require IT approval.',
        'Submit request at: helpdesk@company.com',
        '',
        'Your laptop accesses: Internal admin panels,',
        'Client databases, Company financial systems'
      ]
    }
  },

  // NEW CONGRUENT SAFE SCENARIOS
  {
    id: 'd15',
    domain: 'DDom8', // Personal Device Pairing & Backups
    name: 'Known AirPods Reconnection',
    type: 'connection',
    description: 'Monday 8 AM. You open your AirPods Pro case for a video call. Your company laptop shows the same AirPods name and device identifier that have been paired with it for six months and asks whether to reconnect.',
    warningMessage: 'Previously paired Bluetooth device is ready to reconnect',
    isRisky: false,
    hasWarning: false,
    location: 'Company laptop - your desk',
    source: 'Apple AirPods Pro (your personal headphones, paired 6 months ago)',
    details: 'These are your AirPods, used daily for video calls and paired with this laptop for six months. The device identifier matches the saved Bluetooth entry and the physical AirPods are on your desk.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['ownedDevice', 'previouslyPaired', 'matchingIdentifier', 'physicallyPresent'],
    gistCues: ['Your own AirPods', 'Used them for months', 'Normal reconnection prompt', 'Expected before a call'],
    verbatimCues: ['Paired 6 months ago', 'Device identifier matches saved entry', 'Physical device on desk', 'User opened the case'],
    conflictLevel: 'low',
    complexity: 0.15,
    visualData: {
      type: 'system-notification',
      title: 'Reconnect AirPods Pro?',
      message: 'A previously paired Bluetooth device is available.',
      icon: '🎧',
      buttons: ['Reconnect My AirPods', 'Do Not Reconnect'],
      details: [
        'Device: AirPods Pro',
        'Status: Previously paired',
        'Pairing history: 6 months',
        'Device identifier: Matches saved Bluetooth entry',
        'Last connected: Friday, 4:42 PM'
      ]
    }
  },

  {
    id: 'd16',
    domain: 'DDom7', // Workplace Equipment & Compliance
    name: 'Conference Room TV Screen Mirroring',
    type: 'connection',
    description: 'Thursday 2 PM. You\'re in Conference Room B about to give a presentation. You click the "Cast Screen" button on your laptop. Your laptop shows: "CONF-ROOM-B-TV wants to display content from your screen. Allow?"',
    warningMessage: 'Screen sharing request from conference room display',
    isRisky: false,
    hasWarning: false,
    location: 'Conference Room B - 3rd floor',
    source: 'Samsung Smart TV (CONF-ROOM-B-TV) on company network',
    details: 'You\'re physically standing in Conference Room B right now. The TV on the wall shows "Waiting for connection..." The TV name "CONF-ROOM-B-TV" matches the room label on the door. You can see your laptop name showing on the TV screen as available to connect. This is the same TV you\'ve presented to many times before. Other coworkers are in the room waiting for your presentation. IT maintains all conference room equipment.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['physicallyPresent', 'roomLabelMatches', 'ITmaintained', 'usedBefore'],
    gistCues: ['In the conference room', 'TV name matches room', 'About to present', 'Coworkers waiting'],
    verbatimCues: ['TV shows "waiting for connection"', 'Room label matches TV name', 'You can see the TV', 'Used this TV before for presentations'],
    conflictLevel: 'low',
    complexity: 0.1,
    visualData: {
      type: 'system-notification',
      title: 'Screen Mirroring Request',
      message: 'CONF-ROOM-B-TV wants to display your screen',
      icon: '📺',
      buttons: ['Allow Screen Mirroring', 'Do Not Allow'],
      details: [
        'Display: CONF-ROOM-B-TV',
        'Type: Samsung Smart TV',
        'Location: Conference Room B, 3rd Floor',
        'Network: Company LAN (192.168.20.15)',
        '',
        '✓ You are physically in Conference Room B',
        '✓ TV shows "Waiting for connection" on screen',
        '✓ Room label matches TV name',
        '✓ IT-managed conference room equipment',
        '',
        'This TV will display:',
        '• Your entire screen',
        '• All windows and notifications',
        '• Any audio from your laptop',
        '',
        'You can disconnect anytime by clicking',
        'the Cast icon and selecting "Stop Casting"'
      ]
    }
  },

  // NEW INCONGRUENT SAFE SCENARIO
  {
    id: 'd17',
    domain: 'DDom8', // Personal Device Pairing & Backups
    name: 'Company iPhone Backup Reset',
    type: 'connection',
    description: 'IT re-imaged your work laptop yesterday. When you connect your company-issued iPhone, Finder shows a red warning that 47 GB of inaccessible old local backup files must be deleted before it can create a new encrypted backup. The phone itself is not being erased, and today\'s company-cloud backup is listed as complete.',
    warningMessage: 'Old local backup files must be deleted before creating a new encrypted backup',
    isRisky: false,
    hasWarning: true,
    location: 'Company laptop - your desk',
    source: 'Finder backup for company-issued iPhone',
    details: 'The laptop re-image reset its encryption authorization. The prompt identifies only old, inaccessible backup files on the laptop; it does not list data on the phone for deletion. The current company-cloud backup completed before the local reset.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['recentReimage', 'localFilesOnly', 'cloudBackupComplete', 'companyDevice'],
    gistCues: ['Red warning', 'Delete wording', 'Large amount of backup data', 'Encryption reset'],
    verbatimCues: ['IT re-imaged laptop yesterday', 'Prompt specifies old local backup files', 'Phone is not listed for deletion', 'Company-cloud backup completed today'],
    conflictLevel: 'high',
    complexity: 0.75,
    visualData: {
      type: 'system-notification',
      title: '⚠️ iPhone Backup Authorization Required',
      message: 'This re-imaged computer cannot open its previous encrypted backup files.',
      icon: '📱',
      buttons: ['Delete Old Local Backups and Create New Backup', 'Cancel Backup'],
      details: [
        '⚠️ WARNING: Authorization Reset',
        '',
        'Device: Company-issued iPhone',
        'Laptop status: Re-imaged by IT yesterday',
        'Company-cloud backup: Completed today, 8:14 AM',
        '',
        'To create new backups, iTunes must:',
        '• Delete all existing backup files (47.3 GB)',
        '• Remove old encrypted backup data',
        '• Create fresh backup with new encryption',
        '',
        'Deletion target: Old local backup folder on this laptop',
        'Phone erase: Not requested'
      ]
    }
  },

  // ==========================================
  // DDOM1: SYSTEM UPDATES & IT SECURITY (2 more needed)
  // ==========================================

  // DDom1 - Congruent-Risky
  {
    id: 'd18',
    domain: 'DDom1',
    name: 'Windows Security Update',
    type: 'software',
    description: 'Tuesday 10 AM. Popup appears: "Critical Windows Security Update - CVE-2024-9381. Install immediately to prevent ransomware attack. Your system is at risk." Red warning banner. Update size: 847 MB. Publisher shows "Microsoft Corporation" with blue checkmark.',
    warningMessage: 'Critical security vulnerability detected - update required',
    isRisky: true,
    hasWarning: true,
    location: 'Popup notification on company laptop',
    source: 'windows-security-updates.net',
    details: 'The popup has Microsoft branding and looks official. BUT: Real Windows updates come through Windows Update (Settings > Update), never random popups. Download URL is windows-security-updates.net (NOT microsoft.com). Real updates show KB numbers and come from windowsupdate.microsoft.com. This is ransomware disguised as security update.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['fakeUpdate', 'domainSpoofing', 'ransomware', 'popupDownload'],
    gistCues: ['Critical security warning', 'Red urgent banner', 'Microsoft branding', 'Ransomware prevention'],
    verbatimCues: ['Wrong domain (not microsoft.com)', 'Popup source (not Windows Update)', 'No KB number', 'Manual download (red flag)'],
    conflictLevel: 'low',
    complexity: 0.45,
    visualData: {
      type: 'system-notification',
      title: '⚠️ Critical Security Update Required',
      message: 'Windows Security has detected a critical vulnerability (CVE-2024-9381). Install update immediately.',
      icon: '🛡️',
      buttons: ['Install This Update', 'Do Not Install'],
      details: [
        'Vulnerability: CVE-2024-9381 (Critical)',
        'Threat: Ransomware exploit detected',
        'Publisher: Microsoft Corporation ✓',
        'Update Size: 847 MB',
        'Source: windows-security-updates.net',
        '',
        '⚠️ Verification Check:',
        'Real updates: Come through Windows Update app',
        'This update: Random popup download',
        'Real domain: windowsupdate.microsoft.com',
        'This domain: windows-security-updates.net',
        '',
        'IT Security: "Never install Windows updates',
        'from popup notifications. Always use Settings."'
      ]
    }
  },

  // DDom1 - Incongruent-Risky
  {
    id: 'd19',
    domain: 'DDom1',
    name: 'Antivirus Subscription Renewal',
    type: 'software',
    description: 'Monday 9 AM. Your antivirus (Norton 360) shows notification: "Your subscription expires in 3 days. Renew now for $89.99/year to maintain protection." Official Norton logo, professional design. "Renew Subscription" button leads to norton-renewals.com.',
    warningMessage: 'Antivirus subscription expiring soon',
    isRisky: true,
    hasWarning: false,
    location: 'Company laptop - system tray notification',
    source: 'norton-renewals.com (fake renewal site)',
    details: 'The notification looks identical to real Norton alerts with proper branding. Your company DOES use Norton 360. BUT: Company IT manages all antivirus licenses centrally - employees never pay for renewals personally. The domain is norton-renewals.com (NOT norton.com). Real Norton renewal would go through norton.com or IT would handle it automatically. This is a payment scam targeting Norton users.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['paymentScam', 'domainSpoofing', 'subscriptionFraud', 'creditCardHarvest'],
    gistCues: ['Norton official branding', 'Professional notification', 'Standard renewal process', 'Legitimate software'],
    verbatimCues: ['Wrong domain (norton-renewals.com)', 'Company manages licenses (not employees)', 'IT would auto-renew', 'Personal payment request (red flag)'],
    conflictLevel: 'high',
    complexity: 0.8,
    visualData: {
      type: 'system-notification',
      title: 'Norton 360 - Subscription Expiring',
      message: 'Your Norton 360 subscription expires in 3 days. Renew now to maintain protection.',
      icon: '🛡️',
      buttons: ['Renew Subscription ($89.99/year)', 'Close Without Renewing'],
      details: [
        'Product: Norton 360 Premium',
        'Expiration: March 15, 2025 (3 days)',
        'Renewal Price: $89.99/year',
        'Devices Protected: 5',
        '',
        'Features included:',
        '• Real-time threat protection',
        '• VPN (Secure VPN)',
        '• Password Manager',
        '• Cloud Backup (50 GB)',
        '',
        '⚠️ Verification Check:',
        'Domain: norton-renewals.com (NOT norton.com)',
        'Company IT: Manages all antivirus licenses',
        'Employee Policy: IT auto-renews (no employee payment)',
        '',
        'Red Flags:',
        '• Asks for personal credit card',
        '• Company uses centralized licensing',
        '• Domain is not official Norton site'
      ]
    }
  },

  // ==========================================
  // DDOM2: MALICIOUS SOFTWARE & FAKE UPDATES (2 more needed)
  // ==========================================

  // DDom2 - Congruent-Safe
  {
    id: 'd20',
    domain: 'DDom2',
    name: 'Internal Software Repository',
    type: 'download',
    description: 'Wednesday 2 PM. IT sends email: "Download approved software from internal repository at software.company.com." You access the portal and see Slack, Zoom, Adobe Acrobat listed. All show "IT Approved" badge and verified checksums.',
    warningMessage: 'Software repository managed by IT department',
    isRisky: false,
    hasWarning: false,
    location: 'Internal software portal (software.company.com)',
    source: 'IT Department verified repository',
    details: 'This is your company\'s official internal software repository. All software is vetted by IT, virus-scanned, and comes with verified checksums (SHA-256). The portal is at software.company.com (internal subdomain). IT sent company-wide email this morning about using this portal for all software downloads. Safer than downloading from random websites.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['internalRepository', 'itManaged', 'verifiedChecksums', 'companyPortal'],
    gistCues: ['IT department portal', 'Company domain', 'Verified software', 'IT email sent'],
    verbatimCues: ['software.company.com (internal)', 'SHA-256 checksums', 'IT approval process', 'Email verification'],
    conflictLevel: 'low',
    complexity: 0.2,
    visualData: {
      type: 'software-install',
      title: 'Company Software Repository',
      message: 'Download IT-approved software for your work computer',
      publisher: 'IT Department',
      buttons: ['Download Approved Software', 'Do Not Download'],
      details: [
        'Portal: software.company.com',
        'Managed by: IT Department',
        'All software: Virus scanned and verified',
        '',
        'Available Software:',
        '• Slack Desktop (v4.34.1) - IT Approved ✓',
        '• Zoom Client (v5.16.2) - IT Approved ✓',
        '• Adobe Acrobat DC - IT Approved ✓',
        '',
        'Security Features:',
        '✓ SHA-256 checksum verification',
        '✓ Virus scan before availability',
        '✓ IT security team approval',
        '✓ Automatic security updates'
      ]
    }
  },

  // DDom2 - Incongruent-Safe
  {
    id: 'd21',
    domain: 'DDom2',
    name: 'Company Graphics Driver Update',
    type: 'software',
    description: 'Your company-issued engineering laptop opens Dell Command Update after you requested updates from its Start-menu app. It offers an NVIDIA graphics driver signed by Dell and NVIDIA, but warns that the driver runs with kernel-level system privileges and requires a restart.',
    warningMessage: 'Signed kernel-level graphics driver will be installed',
    isRisky: false,
    hasWarning: true,
    location: 'Company-issued engineering laptop',
    source: 'Dell Command Update',
    details: 'The update was opened by the participant from the installed Dell management app. The package signature matches Dell and NVIDIA, the laptop service tag matches the package, and IT documentation lists Dell Command Update as the approved driver channel.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['approvedUpdateChannel', 'validSignature', 'matchingServiceTag', 'userInitiated'],
    gistCues: ['Kernel-level access', 'System privileges', 'Restart required', 'Low-level driver'],
    verbatimCues: ['Opened from installed Dell app', 'Dell and NVIDIA signatures valid', 'Service tag matches laptop', 'IT-approved update channel'],
    conflictLevel: 'high',
    complexity: 0.75,
    visualData: {
      type: 'software-install',
      title: 'Dell Command Update - NVIDIA Driver',
      message: 'This signed graphics driver runs with kernel-level privileges and requires a restart.',
      publisher: 'Dell Inc. / NVIDIA Corporation',
      buttons: ['Install Verified Driver', 'Do Not Install'],
      details: [
        'Device: Dell Precision 5680',
        'Service tag: Matches this laptop',
        'Package: NVIDIA RTX Graphics Driver',
        'Digital signatures: Dell Inc.; NVIDIA Corporation',
        'Update source: Dell Command Update',
        'Size: 814 MB',
        '',
        '⚠️ System Access Required:',
        '• Kernel-level driver (Ring 0 access)',
        '• Full system privileges',
        '• Restart required',
        '',
        'IT update channel: Dell Command Update',
        'Request initiated from the installed management app'
      ]
    }
  },

  // ==========================================
  // DDOM3: NETWORK & WIFI SECURITY (3 more needed)
  // ==========================================

  // DDom3 - Congruent-Safe
  {
    id: 'd22',
    domain: 'DDom3',
    name: 'Company VPN Connection Required',
    type: 'network',
    description: 'Monday 8 AM working from home. You try to access company SharePoint. Browser shows: "Access Denied. You must connect to Company VPN to access internal resources. Click here to launch VPN client."',
    warningMessage: 'VPN connection required for internal resources',
    isRisky: false,
    hasWarning: false,
    location: 'Home office',
    source: 'Company network gateway (vpn.company.com)',
    details: 'This is your company\'s standard VPN requirement. All internal resources (SharePoint, HR portal, file servers) require VPN when working remotely. The error comes from vpn.company.com (verified company domain). IT sent VPN setup instructions to all remote workers. You have Cisco AnyConnect VPN client already installed. This is normal and expected security practice.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['companyVPN', 'expectedBehavior', 'internalDomain', 'securityPolicy'],
    gistCues: ['Company VPN', 'Internal access requirement', 'Security policy', 'Remote work standard'],
    verbatimCues: ['vpn.company.com (verified)', 'Cisco AnyConnect installed', 'IT setup instructions', 'Standard remote access'],
    conflictLevel: 'low',
    complexity: 0.15,
    visualData: {
      type: 'browser-warning',
      title: 'VPN Connection Required',
      message: 'You must connect to the company VPN to access internal resources.',
      buttons: ['Launch Verified VPN', 'Do Not Launch VPN'],
      details: [
        'Resource: SharePoint Portal',
        'URL: sharepoint.company.com',
        'Access Denied: No VPN connection detected',
        '',
        'Company Security Policy:',
        '• All remote workers must use VPN',
        '• Required for: SharePoint, file servers, HR portal',
        '• VPN Client: Cisco AnyConnect',
        '',
        '✓ Verification:',
        'Gateway: vpn.company.com (Official)',
        'Setup: IT provided instructions',
        'Client: Already installed on your laptop',
        '',
        'Click "Launch VPN Client" to connect',
        'and access company resources.'
      ]
    }
  },

  // DDom3 - Incongruent-Safe
  {
    id: 'd23',
    domain: 'DDom3',
    name: 'Open Airport WiFi Network',
    type: 'network',
    description: 'Thursday 4 PM at an airport waiting for a flight. The WiFi name matches the network printed on gate signage, but it is open and shows a red warning that traffic is not encrypted. Your company VPN is already installed and offers to protect the connection before you join.',
    warningMessage: 'Unsecured public network - traffic not encrypted',
    isRisky: false,
    hasWarning: true,
    location: 'O\'Hare Airport - Gate B12',
    source: 'Airport public WiFi (open network)',
    details: 'This is the airport\'s official public WiFi. The network name matches signage at the gate ("Free WiFi: Airport_Free_WiFi"). Open/unsecured WiFi is NORMAL for airports - they prioritize ease of access over security. While the red warning looks scary, this is legitimate airport WiFi. The warning is HELPFUL (not malicious) - educating you about public WiFi risks. If you use it, avoid banking/passwords or use VPN. This is not an evil twin - it\'s real airport WiFi with proper security warnings.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['legitAirportWiFi', 'matchesSignage', 'properWarning', 'educationalMessage'],
    gistCues: ['Red warning', 'Unsecured network', 'Data visibility threat', 'Scary security message'],
    verbatimCues: ['Official airport WiFi', 'Name matches gate signage', 'Proper security education', 'Not an attack'],
    conflictLevel: 'high',
    complexity: 0.7,
    visualData: {
      type: 'network-selection',
      title: 'Protect Airport WiFi Connection?',
      message: '⚠️ This public network is unencrypted. The company VPN can be enabled before connecting.',
      buttons: ['Enable VPN and Connect', 'Do Not Connect'],
      networks: [
        {
          name: 'Airport_Free_WiFi',
          security: 'Open network · Name matches gate signage',
          strength: '●●●●○'
        }
      ],
      details: [
        'Network: Airport_Free_WiFi',
        'Security: Open (No Password)',
        'Encryption: None',
        'Location: O\'Hare Airport, Gate B12',
        '',
        '⚠️ Security Warning:',
        '• This is an unsecured public network',
        '• Your traffic is NOT encrypted',
        '• Others on network could see your data',
        '',
        'Network name matches gate signage',
        'Common for airports to offer open WiFi',
        '',
        'Company VPN: Installed and ready',
        'VPN gateway: vpn.company.com',
        'Connection option: Encrypt traffic before joining'
      ]
    }
  },

  // DDom3 - Incongruent-Risky
  {
    id: 'd24',
    domain: 'DDom3',
    name: 'Home Router Firmware Update Notification',
    type: 'network',
    description: 'Sunday 11 AM at home. Browser redirect: "Your Netgear router has a critical firmware update. Update now to fix security vulnerabilities. Click here to update: router-firmware-update.net/netgear" Professional Netgear branding.',
    warningMessage: 'Router firmware update available',
    isRisky: true,
    hasWarning: false,
    location: 'Home network',
    source: 'router-firmware-update.net (fake update site)',
    details: 'This looks like a legitimate router update with Netgear branding. BUT: Real router updates happen through the router admin panel (192.168.1.1), NEVER through browser redirects. The domain router-firmware-update.net is NOT Netgear. Real Netgear updates come from netgear.com or router\'s admin interface. This is DNS hijacking or malware trying to install backdoor firmware on your router. Never update router firmware through random browser popups.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['dnshijacking', 'firmwareBackdoor', 'domainSpoofing', 'browserRedirect'],
    gistCues: ['Netgear official branding', 'Security update notification', 'Professional design', 'Critical vulnerability fix'],
    verbatimCues: ['Browser redirect (not router admin)', 'Wrong domain (not netgear.com)', 'Real updates: through 192.168.1.1', 'Firmware backdoor attack'],
    conflictLevel: 'high',
    complexity: 0.85,
    visualData: {
      type: 'browser-warning',
      title: 'Netgear Router - Firmware Update Available',
      message: 'Critical security vulnerabilities detected. Update firmware now to secure your network.',
      buttons: ['Install Firmware from This Page', 'Close This Page'],
      details: [
        'Router: Netgear Nighthawk R7000',
        'Current Firmware: v1.0.11.116',
        'New Firmware: v1.0.11.128',
        'Critical: CVE-2024-8471 (Severe)',
        '',
        'Vulnerabilities Fixed:',
        '• Remote code execution',
        '• Authentication bypass',
        '• Buffer overflow',
        '',
        '⚠️ Verification Check:',
        'This page: router-firmware-update.net',
        'Real updates: Through router admin (192.168.1.1)',
        'Official site: netgear.com',
        '',
        'Red Flags:',
        '✗ Browser redirect (not admin panel)',
        '✗ External domain (not netgear.com)',
        '✗ Popup notification (routers don\'t do this)',
        '',
        'Security Risk: Firmware backdoor installation'
      ]
    }
  },

  // ==========================================
  // DDOM4: MULTI-FACTOR AUTHENTICATION (3 more needed)
  // ==========================================

  // DDom4 - Congruent-Safe
  {
    id: 'd25',
    domain: 'DDom4',
    name: 'Google Authenticator 2FA Setup',
    type: 'mfa',
    description: 'Tuesday 10 AM. IT emails: "Enable 2FA on your work Google account by Friday (company policy). Download Google Authenticator app and scan QR code." Link goes to myaccount.google.com. You scan QR code and 6-digit codes start generating.',
    warningMessage: 'Two-factor authentication setup required by IT',
    isRisky: false,
    hasWarning: false,
    location: 'Company laptop - Google account settings',
    source: 'myaccount.google.com (official Google domain)',
    details: 'Company mandated 2FA for all Google Workspace accounts. The setup page is at myaccount.google.com (verified Google domain). Google Authenticator is Google\'s official 2FA app (millions of users). QR code is generated by Google servers. IT sent company-wide email about this mandate. This is standard 2FA setup and improves security.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['googleOfficial', 'companyMandate', 'standardProcess', 'securityImprovement'],
    gistCues: ['Google official app', 'IT requirement', 'Company policy', 'Standard 2FA setup'],
    verbatimCues: ['myaccount.google.com (verified)', 'Google Authenticator official', 'IT email verified', 'Legitimate security practice'],
    conflictLevel: 'low',
    complexity: 0.2,
    visualData: {
      type: 'system-notification',
      title: 'Enable Two-Factor Authentication',
      message: 'Secure your work Google account with 2FA (required by company IT policy)',
      buttons: ['Set Up Required 2FA', 'Skip Required 2FA'],
      details: [
        'Account: work-email@company.com',
        'Requirement: Company IT Policy',
        'Deadline: Friday, March 15',
        'Method: Google Authenticator app',
        '',
        'Setup Steps:',
        '1. Download Google Authenticator (App Store)',
        '2. Scan QR code below',
        '3. Enter 6-digit code to verify',
        '',
        '✓ Security Benefits:',
        '• Prevents unauthorized access',
        '• Required for company compliance',
        '• Industry best practice',
        '',
        'URL: myaccount.google.com/2fa',
        'Official Google domain ✓'
      ]
    }
  },

  // DDom4 - Incongruent-Safe
  {
    id: 'd26',
    domain: 'DDom4',
    name: 'Okta Verify Push from New Device',
    type: 'mfa',
    description: 'Okta Verify push: "Sign-in attempt from Windows 10 PC in Boston, MA. Approve if this was you." You\'re in Boston on your work laptop. You reformatted it yesterday so Okta sees it as a "new device."',
    warningMessage: 'Sign-in attempt from new device detected',
    isRisky: false,
    hasWarning: true,
    location: 'Boston office - your desk',
    source: 'Okta Verify MFA (company SSO)',
    details: 'This is YOUR sign-in attempt on YOUR laptop after reformatting. Okta tracks device fingerprints (hardware IDs stay same, but OS reinstall = "new device"). Location matches (Boston), device type matches (Windows 10), time matches (you just clicked sign-in). IT reformatted your laptop yesterday as part of standard security practice. The "new device" warning is technically correct but safe to approve - it\'s your known laptop with fresh OS.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['legitimateSignIn', 'correctLocation', 'recentReformat', 'expectedBehavior'],
    gistCues: ['"New device" warning', 'Security alert', 'Unknown device message', 'Potential threat'],
    verbatimCues: ['Your laptop (reformatted yesterday)', 'Correct location (Boston)', 'Correct device type', 'You initiated sign-in'],
    conflictLevel: 'high',
    complexity: 0.75,
    visualData: {
      type: 'mfa-notifications',
      title: 'Okta Verify - Sign-In Request',
      message: 'Sign-in attempt from new device. Approve if this was you.',
      buttons: ['✓ APPROVE', '✗ DENY'],
      notificationCount: 1,
      details: [
        'Account: work-email@company.com',
        'Location: Boston, MA (United States)',
        'IP Address: 192.168.20.45 (Company network)',
        'Device: Windows 10 PC (New Device)',
        'Time: 3:14 PM EST (1 minute ago)',
        '',
        '⚠️ "New Device" Explanation:',
        'IT reformatted your laptop yesterday',
        'Fresh Windows install = new device fingerprint',
        'Same physical laptop, new OS = "new" to Okta',
        '',
        '✓ Verification:',
        'Location: Matches your office (Boston)',
        'Network: Company IP (192.168.20.45)',
        'Device Type: Matches your laptop (Windows 10)',
        'You: Just clicked sign-in 1 minute ago'
      ]
    }
  },

  // DDom4 - Incongruent-Risky
  {
    id: 'd27',
    domain: 'DDom4',
    name: 'Microsoft Account Verification Email',
    type: 'mfa',
    description: 'Monday 2 PM. Email from "Microsoft Account Team <account-security@microsoft.com>": "We detected unusual sign-in activity. Verify your identity to keep your account secure." Link: "Verify Account". Professional Microsoft design, correct logo, no obvious typos.',
    warningMessage: 'Account verification requested due to suspicious activity',
    isRisky: true,
    hasWarning: false,
    location: 'Work email inbox',
    source: 'account-security@microsoft.com (spoofed email)',
    details: 'Email looks identical to real Microsoft security alerts. BUT: Sender is spoofed (display says microsoft.com but actual sender is different). Real Microsoft never asks you to "verify account" via email link - they use SMS or authenticator app. Hovering over "Verify Account" button shows URL: microsft-verify.com (missing "o" - typosquatting). This is credential phishing targeting Microsoft 365 users.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['credentialPhishing', 'emailSpoofing', 'typosquatting', 'microsoftImpersonation'],
    gistCues: ['Microsoft official branding', 'Security team sender', 'Professional email design', 'Account protection'],
    verbatimCues: ['Spoofed sender address', 'Link to microsft-verify.com (typo)', 'Real Microsoft uses SMS/app', 'Never email verification links'],
    conflictLevel: 'high',
    complexity: 0.9,
    visualData: {
      type: 'email-attachment-preview',
      title: 'Microsoft Account Security Alert',
      sender: 'Microsoft Account Team',
      subject: 'Unusual sign-in activity detected',
      message: 'We detected unusual sign-in activity on your Microsoft account. To keep your account secure, please verify your identity.',
      buttons: ['Verify Account', 'Report as Spam'],
      details: [
        'From: Microsoft Account Team',
        'Email: account-security@microsoft.com',
        'Subject: Unusual sign-in activity detected',
        '',
        'Message Content:',
        '"We detected a sign-in attempt from an',
        'unfamiliar location. To protect your account,',
        'please verify your identity."',
        '',
        '⚠️ Verification Check:',
        'Hover over button shows: microsft-verify.com',
        'Missing "o": microsft (not microsoft)',
        'Real Microsoft: Uses SMS or Authenticator app',
        'Never sends: Email verification links',
        '',
        'Red Flags:',
        '✗ Typosquatting domain (microsft vs microsoft)',
        '✗ Email verification request (Microsoft never does this)',
        '✗ Generic "unusual activity" claim'
      ]
    }
  },

  // ==========================================
  // DDOM5: PHYSICAL DEVICES & USB (3 more needed)
  // ==========================================

  // DDom5 - Congruent-Safe
  {
    id: 'd28',
    domain: 'DDom5',
    name: 'IT Department USB Drive for Data Transfer',
    type: 'connection',
    description: 'Thursday 1 PM. IT technician comes to your desk: "I\'m here to migrate your files to new laptop. I have an encrypted IT-approved USB drive (SanDisk Extreme Pro with hardware encryption). This has IT asset tag #4729. Need to plug it in to transfer your data."',
    warningMessage: 'IT technician requesting USB access for data migration',
    isRisky: false,
    hasWarning: false,
    location: 'Your desk - IT scheduled visit',
    source: 'IT Department (verified technician)',
    details: 'You scheduled this laptop upgrade with IT last week. Technician has company ID badge with photo matching his face. USB drive has official IT asset tag sticker (#4729 - you can verify in IT asset database). IT uses SanDisk Extreme Pro drives with hardware encryption for secure data transfers. This is standard IT procedure for laptop migrations.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['scheduledITvisit', 'verifiedTechnician', 'ITassetTag', 'standardProcedure'],
    gistCues: ['IT technician badge', 'Scheduled laptop upgrade', 'Company asset tag', 'Official data transfer'],
    verbatimCues: ['Appointment scheduled last week', 'ID badge verified', 'Asset tag #4729 in database', 'Hardware-encrypted USB'],
    conflictLevel: 'low',
    complexity: 0.15,
    visualData: {
      type: 'usb-dialog',
      title: 'IT Data Migration - USB Drive Access',
      message: 'IT technician requests USB drive access for scheduled laptop migration',
      icon: '💼',
      buttons: ['Allow Scheduled IT Transfer', 'Do Not Allow'],
      details: [
        'Technician: Mike Johnson (IT Department)',
        'ID Badge: Verified ✓',
        'Appointment: Scheduled March 10 (last week)',
        '',
        'USB Drive Details:',
        '• Model: SanDisk Extreme Pro (256GB)',
        '• Encryption: Hardware AES-256',
        '• IT Asset Tag: #4729 (verified in database)',
        '',
        '✓ Security Verification:',
        'Technician ID matches company photo',
        'Laptop migration scheduled with you',
        'USB has official IT asset tag',
        'Standard IT migration procedure',
        '',
        'Purpose: Transfer your files to new laptop',
        'Encryption: Hardware-level (secure)'
      ]
    }
  },

  // DDom5 - Congruent-Risky
  {
    id: 'd29',
    domain: 'DDom5',
    name: 'USB Flash Drive Found in Parking Lot',
    type: 'connection',
    description: 'Tuesday 8 AM arriving at work. You find a USB flash drive in the parking lot near your car. It has a label: "EXECUTIVE SALARIES 2024 - CONFIDENTIAL" in red marker. No owner identification. Curious about salary data.',
    warningMessage: 'Unknown USB device found in parking lot',
    isRisky: true,
    hasWarning: true,
    location: 'Company parking lot',
    source: 'Unknown USB flash drive (potential attack)',
    details: 'This is a classic "USB drop attack" - attackers deliberately leave infected USB drives in parking lots hoping employees will plug them in out of curiosity. The "Executive Salaries" label is social engineering bait. When plugged in, the drive can: auto-run malware (via Windows AutoRun), install keyloggers, create backdoor access, or encrypt files (ransomware). Never plug in found USB drives. Report to IT Security instead.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['usbDropAttack', 'socialEngineering', 'curiosityBait', 'unknownDevice'],
    gistCues: ['Found device', 'No owner', 'Parking lot location', 'Confidential salary data bait'],
    verbatimCues: ['USB drop attack (known tactic)', 'Auto-run malware risk', 'Social engineering label', 'Company security policy: report to IT'],
    conflictLevel: 'low',
    complexity: 0.3,
    visualData: {
      type: 'usb-dialog',
      title: 'Unknown USB Drive Found',
      message: 'Should you plug this USB drive into your laptop?',
      icon: '💾',
      buttons: ['Plug In Found USB Drive', 'Do Not Plug It In'],
      details: [
        'Device: Generic USB flash drive (32GB)',
        'Label: "EXECUTIVE SALARIES 2024 - CONFIDENTIAL"',
        'Location Found: Parking lot near your car',
        'Owner: Unknown (no identification)',
        '',
        '⚠️ Security Threat: USB Drop Attack',
        'Tactic: Leave infected USB in public places',
        'Bait: Curious label ("Executive Salaries")',
        'Goal: Employee plugs it in out of curiosity',
        '',
        'Possible Malware:',
        '• AutoRun malware (executes on plug-in)',
        '• Keylogger installation',
        '• Ransomware (encrypts your files)',
        '• Backdoor for network access',
        '',
        'Company Security Policy:',
        '❌ Never plug in unknown USB devices',
        '✓ Report found devices to IT Security',
        '✓ IT will safely examine on isolated system'
      ]
    }
  },

  // DDom5 - Incongruent-Safe
  {
    id: 'd30',
    domain: 'DDom5',
    name: 'Coworker Personal Phone Charger Request',
    type: 'connection',
    description: 'Your coworker Sarah asks: "My phone died - can I borrow your USB cable for 15 minutes? I need to call my kid\'s school." She shows her iPhone and sits at the desk next to you.',
    warningMessage: 'Coworker requesting to use your USB cable',
    isRisky: false,
    hasWarning: true,
    location: 'Your desk - coworker request',
    source: 'Known coworker (Sarah - 2 years)',
    details: 'This is your long-time coworker Sarah who sits next to you. You know her well (worked together 2 years). She has the same iPhone model as you. She\'s asking to borrow YOUR cable to charge HER phone - not plugging unknown cable into your laptop. She sits right next to you (not leaving with your cable). While technically "sharing cables" has risks, this is: known person, same room, short duration, normal workplace favor.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['knownCoworker', 'sameRoom', 'shortDuration', 'normalRequest'],
    gistCues: ['USB sharing request', 'Security training warned about this', 'Cable could be compromised', 'Technical security risk'],
    verbatimCues: ['2-year coworker (known and trusted)', 'She uses your cable (not her cable)', 'Same room (supervised)', 'Normal workplace favor'],
    conflictLevel: 'high',
    complexity: 0.7,
    visualData: {
      type: 'usb-dialog',
      title: 'Coworker USB Cable Request',
      message: 'Coworker Sarah asks to borrow your charging cable',
      icon: '🔌',
      buttons: ['Lend My Cable', 'Do Not Lend My Cable'],
      details: [
        'Requester: Sarah Johnson (Marketing)',
        'Relationship: Coworker for 2 years',
        'Desk: Sits next to you',
        'Request: Borrow YOUR cable for HER iPhone',
        'Duration: 15 minutes',
        'Reason: Phone died, needs to call school',
        '',
        'Context:',
        '• Same iPhone model as yours',
        '• Sitting at desk next to you (visible)',
        '• Not leaving your area with cable',
        '',
        'Security Considerations:',
        '⚠️ Policy: "Don\'t share USB cables" (training)',
        '✓ Known coworker (2 years)',
        '✓ Your cable → her phone (not her cable → your laptop)',
        '✓ Supervised (same room)',
        '✓ Short duration (15 min)',
        '',
        'Risk Level: Low (known person, normal favor)'
      ]
    }
  },

  // ==========================================
  // DDOM6: BROWSER EXTENSIONS & PLUGINS (3 more needed)
  // ==========================================

  // DDom6 - Congruent-Safe
  {
    id: 'd31',
    domain: 'DDom6',
    name: 'Grammarly Writing Assistant Extension',
    type: 'software',
    description: 'Monday 11 AM. You\'re writing emails and see Chrome Web Store recommendation: "Grammarly - Writing Assistant. 10M+ users, 4.6 stars. Recommended by The Verge, TechCrunch. Fix grammar and spelling mistakes as you type." It\'s the #1 productivity extension.',
    warningMessage: 'Extension requests permission to read and modify text on websites',
    isRisky: false,
    hasWarning: true,
    location: 'Company laptop - Chrome Web Store',
    source: 'Grammarly Inc. (verified developer)',
    details: 'The listing shows a verified Grammarly publisher and a large established user base. The organization\'s software catalog lists this exact extension as approved. Its text-access permission is used to check writing in supported fields.',
    action: null,
    congruence: 'congruent',
    safetyIndicators: ['legitimateTool', 'majorCompany', 'companyRecommended', 'millionsOfUsers'],
    gistCues: ['Major productivity tool', '10M users', 'Company recommended', 'Professional writing aid'],
    verbatimCues: ['Verified publisher', 'Large established user base', 'Text access supports writing checks', 'Exact extension appears in IT catalog'],
    conflictLevel: 'low',
    complexity: 0.25,
    visualData: {
      type: 'vscode-extension',
      title: 'Add Grammarly to Chrome?',
      message: 'Grammarly will help you write mistake-free emails and documents',
      extensionName: 'Grammarly - Writing Assistant',
      publisher: 'Grammarly Inc. (Verified)',
      buttons: ['Install Approved Extension', 'Do Not Install'],
      details: [
        'Extension: Grammarly Writing Assistant',
        'Developer: Grammarly Inc.',
        'Users: 10,000,000+',
        'Rating: ★★★★★ (4.6/5 - 87,423 reviews)',
        '',
        'This extension can:',
        '• Read and modify text on websites',
        '• Access clipboard data',
        '',
        '✓ Why these permissions are needed:',
        'Text access: To check grammar/spelling',
        'Clipboard: For copy/paste corrections',
        '',
        'Organization software catalog: Approved',
        'Catalog publisher: Grammarly Inc.',
        'Catalog extension ID: Matches this listing'
      ]
    }
  },

  // DDom6 - Congruent-Risky
  {
    id: 'd32',
    domain: 'DDom6',
    name: 'Download Manager Pro Extension',
    type: 'software',
    description: 'Thursday 2 PM. Popup while downloading a large file: "Slow download detected. Install Download Manager Pro to boost download speed by 500%. Free extension - 50,000+ users." Chrome Web Store link. Extension shows 4.2 stars.',
    warningMessage: 'Extension claims to accelerate download speeds',
    isRisky: true,
    hasWarning: true,
    location: 'Company laptop - popup advertisement',
    source: 'Download Manager Pro (adware extension)',
    details: 'This is a scam extension. Download speed cannot be "boosted" by software - it depends on your internet connection and server. Extensions claiming speed boosts are adware/malware. "Download Manager Pro" requests permissions to "read all data on websites" (steal cookies/passwords). The 50,000 users and 4.2 stars are likely fake reviews. Real download managers like Internet Download Manager don\'t use Chrome extensions.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['adwareExtension', 'fakeSpeedClaims', 'excessivePermissions', 'fakeReviews'],
    gistCues: ['500% speed boost claim', 'Popup advertisement', 'Too good to be true', 'Suspicious promise'],
    verbatimCues: ['Download speed can\'t be software-boosted', 'Requires all website access', 'Popup source (not user search)', 'Fake review indicators'],
    conflictLevel: 'low',
    complexity: 0.4,
    visualData: {
      type: 'vscode-extension',
      title: 'Download Manager Pro - Install?',
      message: 'Boost download speeds by 500%! Free extension.',
      extensionName: 'Download Manager Pro',
      publisher: 'FastDownload Solutions',
      buttons: ['Install This Extension', 'Do Not Install'],
      details: [
        'Extension: Download Manager Pro',
        'Developer: FastDownload Solutions (Unverified)',
        'Users: 50,284',
        'Rating: ★★★★☆ (4.2/5 - 1,847 reviews)',
        '',
        'Claims: "500% faster downloads"',
        '',
        'This extension can:',
        '• Read and change all data on websites',
        '• See browsing history',
        '• Manage downloads',
        '',
        '⚠️ Reality Check:',
        'Download speed depends on:',
        '• Your internet connection speed',
        '• Server upload speed',
        '• NOT software (can\'t be "boosted")',
        '',
        'Red Flags:',
        '✗ Impossible claims (500% speed boost)',
        '✗ Popup advertisement (suspicious source)',
        '✗ Excessive permissions (read all websites)',
        '✗ Likely fake reviews'
      ]
    }
  },

  // DDom6 - Incongruent-Safe
  {
    id: 'd33',
    domain: 'DDom6',
    name: 'Organization-Approved Password Manager',
    type: 'software',
    description: 'Friday 3 PM. You open the browser-extension link from your organization\'s internal software catalog. The verified "Organization Password Manager" listing warns that it can read and change data on websites so it can detect login forms and fill saved work credentials.',
    warningMessage: 'Password-manager extension can read and modify website form data',
    isRisky: false,
    hasWarning: true,
    location: 'Company laptop - Chrome Web Store',
    source: 'Internal software catalog - verified publisher',
    details: 'The internal software catalog links to this exact extension identifier and verified publisher. The listed permissions support login-form detection and credential autofill. IT documentation names the extension as the organization-approved password manager.',
    action: null,
    congruence: 'incongruent',
    safetyIndicators: ['internalCatalog', 'verifiedPublisher', 'matchingExtensionId', 'itApproved'],
    gistCues: ['Read website data warning', 'Credential access', 'Broad permissions', 'Security concern'],
    verbatimCues: ['Opened from internal catalog', 'Verified publisher', 'Extension ID matches catalog', 'Permissions correspond to autofill function'],
    conflictLevel: 'high',
    complexity: 0.8,
    visualData: {
      type: 'vscode-extension',
      title: 'Organization Password Manager - Install?',
      message: '⚠️ This extension can read and change data in website login forms',
      extensionName: 'Organization Password Manager',
      publisher: 'Verified Enterprise Publisher',
      buttons: ['Install Approved Password Manager', 'Do Not Install'],
      details: [
        'Extension: Organization Password Manager',
        'Publisher: Verified Enterprise Publisher',
        'Extension ID: opm-enterprise-4821',
        '',
        '⚠️ Permissions Warning:',
        '• Read and change data in login forms',
        '• Access saved work credentials for autofill',
        '',
        'Opened from: Internal software catalog',
        'Catalog extension ID: opm-enterprise-4821',
        'IT catalog status: Approved',
        'Purpose: Work-account password storage and autofill'
      ]
    }
  },

  // ==========================================
  // DDOM7: WORKPLACE EQUIPMENT & COMPLIANCE (2 more needed)
  // ==========================================

  // DDom7 - Congruent-Risky
  {
    id: 'd34',
    domain: 'DDom7',
    name: 'Printer Configuration Popup',
    type: 'software',
    description: 'Monday 9 AM printing document. Popup appears: "Canon Printer Driver Update Required - Your printer won\'t work without this update. Download: printer-drivers-update.com/canon" Has Canon logo and professional design.',
    warningMessage: 'Printer driver update required',
    isRisky: true,
    hasWarning: true,
    location: 'Company laptop while printing',
    source: 'printer-drivers-update.com (fake driver site)',
    details: 'This is fake printer driver malware. Real printer driver updates come through Windows Update or Canon\'s official website (canon.com), not random popups. The domain printer-drivers-update.com is NOT Canon. Company printers are managed by IT - they push driver updates automatically. Clicking this installs malware disguised as printer software.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['fakeDriverUpdate', 'domainSpoofing', 'printMalware', 'popupDownload'],
    gistCues: ['Printer not working', 'Driver update needed', 'Canon branding', 'Technical requirement'],
    verbatimCues: ['Wrong domain (not canon.com)', 'IT manages printer drivers', 'Windows Update handles drivers', 'Popup source (suspicious)'],
    conflictLevel: 'low',
    complexity: 0.35,
    visualData: {
      type: 'system-notification',
      title: 'Canon Printer - Driver Update Required',
      message: 'Your printer cannot function without the latest driver. Download now.',
      icon: '🖨️',
      buttons: ['Download Driver Update', 'Cancel'],
      details: [
        'Printer: Canon imageRUNNER ADVANCE',
        'Status: Driver outdated',
        'Download: printer-drivers-update.com/canon',
        '',
        '⚠️ Verification Check:',
        'This domain: printer-drivers-update.com',
        'Official Canon: canon.com or usa.canon.com',
        '',
        'Real driver updates:',
        '• Windows Update (automatic)',
        '• Canon.com (official site)',
        '• IT managed (for company printers)',
        '',
        'Red Flags:',
        '✗ Popup notification (not Windows Update)',
        '✗ External domain (not canon.com)',
        '✗ Company printers managed by IT'
      ]
    }
  },

  // DDom7 - Incongruent-Risky
  {
    id: 'd35',
    domain: 'DDom7',
    name: 'Office 365 License Compliance Scan',
    type: 'software',
    description: 'Tuesday 11 AM. Email from "IT Compliance <compliance@company.com>": "Microsoft requires annual Office 365 license audit. Run compliance scanner to verify your license. Failure to comply by Friday will result in account suspension." Attachment: Office365_Compliance_Scanner.exe',
    warningMessage: 'Compliance scan required for Microsoft Office licensing',
    isRisky: true,
    hasWarning: false,
    location: 'Work email - appears to be from IT',
    source: 'Spoofed IT email with malware attachment',
    details: 'Email appears to be from company IT with official compliance language. BUT: IT never sends executable attachments via email (policy violation). Real Office 365 license audits are done by IT centrally (employees never run scanners). The attachment "Office365_Compliance_Scanner.exe" is ransomware. Display shows compliance@company.com but actual sender email is different (spoofed). Company policy: IT never sends .exe files via email.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['emailSpoofing', 'ransomwareAttachment', 'executiveFile', 'complianceScam'],
    gistCues: ['IT compliance email', 'Official company sender', 'Microsoft audit', 'Professional compliance language'],
    verbatimCues: ['Spoofed email address', '.exe attachment (policy violation)', 'IT manages licenses centrally', 'Never sends executables via email'],
    conflictLevel: 'high',
    complexity: 0.85,
    visualData: {
      type: 'email-attachment-preview',
      title: 'Office 365 License Compliance Required',
      sender: 'IT Compliance',
      subject: 'ACTION REQUIRED: Office 365 License Audit',
      message: 'Microsoft requires annual license verification. Run attached compliance scanner by Friday or face account suspension.',
      attachmentType: 'Office365_Compliance_Scanner.exe (2.4 MB)',
      buttons: ['Download Attachment', 'Report Phishing'],
      details: [
        'From: IT Compliance <compliance@company.com>',
        'Subject: Office 365 License Audit Required',
        'Attachment: Office365_Compliance_Scanner.exe',
        '',
        'Message: "Microsoft policy requires annual',
        'license verification. Download and run the',
        'attached compliance scanner. Failure to',
        'comply by Friday will result in account',
        'suspension per company IT policy."',
        '',
        '⚠️ Verification Check:',
        'Sender: compliance@company.com (spoofed)',
        'Attachment: .exe file (executable)',
        '',
        'Red Flags:',
        '✗ IT NEVER sends .exe files via email',
        '✗ License audits done centrally by IT',
        '✗ Employees never run scanners',
        '✗ .exe attachment = malware delivery'
      ]
    }
  },

  // ==========================================
  // DDOM8: PERSONAL DEVICE PAIRING & BACKUPS (2 more needed)
  // ==========================================

  // DDom8 - Congruent-Risky
  {
    id: 'd36',
    domain: 'DDom8',
    name: 'Bluetooth Keyboard Pairing Request',
    type: 'connection',
    description: 'Wednesday 3 PM working at coffee shop. Your laptop shows: "Bluetooth device \'John\'s Magic Keyboard\' wants to pair. Click Allow to connect." You don\'t have a Bluetooth keyboard and don\'t know anyone named John. You\'re in public coffee shop.',
    warningMessage: 'Unknown Bluetooth device requesting pairing',
    isRisky: true,
    hasWarning: true,
    location: 'Public coffee shop',
    source: 'Unknown Bluetooth device (potential attack)',
    details: 'This is a Bluetooth attack (BlueBorne or Bluesnarfing). Attacker nearby is trying to pair with your laptop to: steal files, inject keystrokes, create backdoor access, or eavesdrop. Device name "John\'s Magic Keyboard" is fake - designed to seem harmless. You don\'t own a Bluetooth keyboard. You\'re in public coffee shop (attacker could be anyone nearby). Never accept unknown Bluetooth pairing requests, especially in public spaces.',
    action: null,
    congruence: 'congruent',
    threatIndicators: ['bluetoothAttack', 'bluesnarfing', 'publicLocation', 'unknownDevice'],
    gistCues: ['Unknown device', 'Public coffee shop', 'Don\'t own Bluetooth keyboard', 'Suspicious pairing request'],
    verbatimCues: ['Bluetooth attack (BlueBorne/Bluesnarfing)', 'Attacker nearby in public', 'Can steal files/inject commands', 'Never pair with unknown devices'],
    conflictLevel: 'low',
    complexity: 0.3,
    visualData: {
      type: 'system-notification',
      title: 'Bluetooth Pairing Request',
      message: '"John\'s Magic Keyboard" wants to connect to your computer',
      icon: '⌨️',
      buttons: ['Allow', 'Deny'],
      details: [
        'Device: John\'s Magic Keyboard',
        'Type: Bluetooth Keyboard',
        'Location: Within Bluetooth range (~30 feet)',
        '',
        'Your Situation:',
        '• You don\'t own a Bluetooth keyboard',
        '• You don\'t know anyone named "John"',
        '• You\'re at a public coffee shop',
        '',
        '⚠️ Bluetooth Attack (BlueBorne/Bluesnarfing):',
        'Tactic: Attacker sends pairing request',
        'Location: Public spaces (coffee shops, airports)',
        'Goal: Access your laptop remotely',
        '',
        'If accepted, attacker can:',
        '• Steal files and data',
        '• Inject keystrokes',
        '• Install malware',
        '• Eavesdrop on activity',
        '',
        'Security Rule: NEVER pair with unknown',
        'Bluetooth devices, especially in public'
      ]
    }
  },

  // DDom8 - Incongruent-Risky
  {
    id: 'd37',
    domain: 'DDom8',
    name: 'Apple Watch Unlock Feature for MacBook',
    type: 'connection',
    description: 'Friday 10 AM. macOS notification: "Unlock your Mac with Apple Watch? Your Apple Watch can automatically unlock your MacBook when you\'re nearby. No password needed. Enable this feature?" You have an Apple Watch (wearing it now) and a company MacBook.',
    warningMessage: 'Apple Watch auto-unlock feature available',
    isRisky: true,
    hasWarning: false,
    location: 'Company MacBook - macOS system settings',
    source: 'macOS system feature (Apple Watch Unlock)',
    details: 'Apple Watch Unlock is a REAL Apple feature that auto-unlocks your Mac when you\'re nearby. While convenient, it bypasses password protection - if someone steals your laptop while you\'re nearby (e.g., at coffee shop), your Watch will auto-unlock it. Company IT security policy forbids auto-unlock features on work laptops - they require password entry every time. This is a legitimate Apple feature but violates company security policy.',
    action: null,
    congruence: 'incongruent',
    threatIndicators: ['autoUnlockRisk', 'proximityBypass', 'policyViolation', 'physicalSecurityRisk'],
    gistCues: ['Apple official feature', 'Convenient unlock', 'Your own Apple Watch', 'Seamless Mac integration'],
    verbatimCues: ['Company policy violation', 'Bypasses password protection', 'Risk if laptop stolen while nearby', 'IT forbids auto-unlock'],
    conflictLevel: 'high',
    complexity: 0.9,
    visualData: {
      type: 'system-notification',
      title: 'Unlock Mac with Apple Watch?',
      message: 'Enable automatic unlock when your Apple Watch is nearby. No password required.',
      icon: '⌚',
      buttons: ['Enable Auto-Unlock', 'No Thanks'],
      details: [
        'Feature: Apple Watch Auto-Unlock',
        'Your Watch: Apple Watch Series 8 (paired)',
        'Your Mac: MacBook Pro (company laptop)',
        '',
        'How it works:',
        '• Mac detects your Watch nearby (Bluetooth)',
        '• Automatically unlocks without password',
        '• Convenient for frequent lock/unlock',
        '',
        'Used by millions of Apple Watch owners',
        '',
        '⚠️ Company Security Policy:',
        'IT Policy SEC-2024-12: "Auto-unlock features',
        'are FORBIDDEN on company laptops."',
        '',
        'Security Risk:',
        '• Laptop stolen while you\'re nearby',
        '  (coffee shop, office) = auto-unlocks',
        '• Bypasses password protection',
        '• Violates company security policy',
        '',
        'Recommendation: Keep requiring password'
      ]
    }
  }
];

// Helper: Seeded shuffle using Fisher-Yates algorithm
// Provides deterministic randomization based on participant ID
function seededShuffle<T>(array: T[], seed: number): T[] {
  const arr = [...array]; // Copy to avoid mutation
  let currentSeed = seed;

  // Mulberry32 PRNG (pseudorandom number generator)
  const random = () => {
    currentSeed |= 0;
    currentSeed = (currentSeed + 0x6d2b79f5) | 0;
    let t = Math.imul(currentSeed ^ (currentSeed >>> 15), currentSeed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Fisher-Yates shuffle with seeded random
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

// Function to get balanced scenarios: 2 of each condition (2×2×2×2)
// GUARANTEES: 2 Congruent-Safe, 2 Congruent-Risky, 2 Incongruent-Safe, 2 Incongruent-Risky
// Uses stratified random sampling to ensure uniform distribution across participants
// Function to get balanced scenarios using Latin Square counterbalancing
// NEW APPROACH: Shows 4 scenarios (1 per condition) from 4 domains, with guaranteed cross-participant balance
// GUARANTEES:
// - Each participant sees exactly 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky
// - Across every 8 participants, each domain appears exactly once with each condition (perfect balance)
// - Overall: Each domain appears 12.5% of the time (4/8 rotation patterns)
// - Within-domain: Each condition appears 25% of the time (1/4 conditions)
export function getScenariosByCongruence(participantId?: string): DeviceScenario[] {
  // Create seed from participant ID for consistent randomization
  let seedValue = 0;
  if (participantId) {
    for (let i = 0; i < participantId.length; i++) {
      seedValue += participantId.charCodeAt(i);
    }
  } else {
    seedValue = Math.floor(Math.random() * 1000000);
  }

  // Group scenarios by domain
  const domainMap: { [key: string]: DeviceScenario[] } = {};
  deviceScenarios.forEach(scenario => {
    if (!domainMap[scenario.domain]) {
      domainMap[scenario.domain] = [];
    }
    domainMap[scenario.domain].push(scenario);
  });

  // All 8 domains for device scenarios
  const allDomains = ['DDom1', 'DDom2', 'DDom3', 'DDom4', 'DDom5', 'DDom6', 'DDom7', 'DDom8'];

  // All 4 conditions
  const allConditions = ['Congruent-Safe', 'Congruent-Risky', 'Incongruent-Safe', 'Incongruent-Risky'];

  // Calculate rotation index from participant ID (0-7)
  // This ensures perfect counterbalancing across every 8 participants
  const rotationIndex = seedValue % 8;

  // Latin Square rotation patterns
  // Each pattern selects 4 domains and assigns one condition to each
  // Pattern 0: DDom1(CS), DDom2(CR), DDom3(IS), DDom4(IR)
  // Pattern 1: DDom2(CS), DDom3(CR), DDom4(IS), DDom5(IR)
  // ...and so on, cycling through domains
  const conditionAssignments = [];
  for (let i = 0; i < 4; i++) {
    const domainIndex = (rotationIndex + i) % 8; // Cycle through domains
    const domain = allDomains[domainIndex];
    const condition = allConditions[i]; // Assign conditions in order: CS, CR, IS, IR

    conditionAssignments.push({
      domains: [domain],
      condition: condition
    });
  }

  // Select scenarios based on Latin Square assignments
  const selected: DeviceScenario[] = [];

  conditionAssignments.forEach(assignment => {
    assignment.domains.forEach(domain => {
      const domainScenarios = domainMap[domain];

      // Find scenario matching the assigned condition
      const [congruence, risk] = assignment.condition.split('-');
      const matchingScenario = domainScenarios.find(s => {
        const isCongruent = s.congruence === 'congruent';
        const isSafe = !s.isRisky;

        const matchesCongruence =
          (congruence === 'Congruent' && isCongruent) ||
          (congruence === 'Incongruent' && !isCongruent);

        const matchesRisk =
          (risk === 'Safe' && isSafe) ||
          (risk === 'Risky' && !isSafe);

        return matchesCongruence && matchesRisk;
      });

      if (matchingScenario) {
        selected.push(matchingScenario);
      } else {
        console.error(`No matching scenario found for ${domain} with condition ${assignment.condition}`);
      }
    });
  });

  // Shuffle the selected scenarios so they're not always in the same order
  const shuffledSelected = seededShuffle(selected, seedValue + 2000);

  // Verify we have exactly 4 scenarios
  if (shuffledSelected.length !== 4) {
    throw new Error(`Expected 4 scenarios, got ${shuffledSelected.length}. Participant: ${participantId}`);
  }

  // Verify distribution: exactly 1 of each condition
  const counts = {
    'Congruent-Safe': 0,
    'Congruent-Risky': 0,
    'Incongruent-Safe': 0,
    'Incongruent-Risky': 0
  };

  shuffledSelected.forEach(s => {
    const isCongruent = s.congruence === 'congruent';
    const isSafe = !s.isRisky;
    const key = `${isCongruent ? 'Congruent' : 'Incongruent'}-${isSafe ? 'Safe' : 'Risky'}`;
    counts[key as keyof typeof counts]++;
  });

  // Assert exactly 1 of each condition
  Object.entries(counts).forEach(([condition, count]) => {
    if (count !== 1) {
      throw new Error(`Expected 1 scenario for ${condition}, got ${count}. Participant: ${participantId}`);
    }
  });

  return shuffledSelected;
}

// Deprecated: balanceScenarios is no longer needed
// getScenariosByCongruence now guarantees balanced 2×2×2×2 distribution
export function balanceScenarios(scenarios: DeviceScenario[]): DeviceScenario[] {
  return scenarios;
}
